import React from 'react'
import { Input, Radio, RadioGroup, Box, UncontrolledCheckbox as Checkbox, MenuBox, SearchInputGroup, Icon } from '@maui/elements'
import { TIME_SLOTS, TIME_ZONES, WEEKDAYS } from '@/utilities'
import { LayoutColumn, LayoutWrapper, Label } from '../common'
import { DropDownSelect } from '../../fields/DropDownSelect'
import { Controller } from 'react-hook-form'
import { useDraft } from '@maui/hooks'
import { ControlledCheckbox } from '../report/ReportFormContent'

const WeeklyPreference = ({ control }) => {
  return (
    <LayoutColumn gap='spaceGapSmall'>
      <Label htmlFor='deliveryDays'>
        Days Of The Week
      </Label>
      <Controller
        control={control}
        name='deliveryDays'
        rules={{ required: true }}
        render={({
          field: { onChange, value, ref },
        }) => (
          <RadioGroup
            onChange={onChange}
            value={parseInt(value) || 0}
            ref={ref}
            display="flex"
            gap="spaceGapXLarge"
          >
            {
              WEEKDAYS.map(({ label, value }, index) => (
                <Radio key={value} value={index} >
                  {label}
                </Radio>
              ))
            }
          </RadioGroup>
        )
        }
      />
    </LayoutColumn>
  )
}

const DailyPreferences = React.memo(({ error, register }) => (
  <LayoutColumn gap='spaceGapSmall'>
    <Label htmlFor='deliveryDays'>
      Daily Delivery Preferences
    </Label>
    {
      error &&
      <Box color="colorFeedbackError" fontStyle="italic" display="flex" flexDirection="row" alignItems="center" gap="spaceGapSmall">
        <Icon name="alert-circle" />
        Please make a selection
      </Box>
    }
    <Box display="flex" flexDirection="row" gap="spaceGapXLarge" alignItems="center">
      {
        WEEKDAYS.map(({ label, value }, index) => (
          <Checkbox key={value} value={index} {...register('deliveryDays', { required: true })} >
            {label}
          </Checkbox>
        ))
      }
    </Box>
  </LayoutColumn>
))

DailyPreferences.displayName = 'DailyPreferences'

const Frequency = ({ control, register, deliveryFreq, errors, clearErrors, setValue }) => {
  const [frequencyValue, setFrequencyValue] = useDraft(deliveryFreq)

  return (
    <>
      <LayoutColumn gap='spaceGapSmall'>
        <Label htmlFor='deliveryFreq'>
          Frequency
        </Label>
        <Controller
          control={control}
          name='deliveryFreq'
          render={({
            field: { onChange, value, ref },
          }) => (
            <RadioGroup
              onChange={(e) => {
                onChange(e)
                setFrequencyValue(e.target.value)
                clearErrors('deliveryDays')
                setValue('deliveryDays', '0')
              }}
              value={value}
              ref={ref}
              display="flex"
              gap="spaceGapXLarge"
            >
              <Radio value="DAILY">
                Daily
              </Radio>
              <Radio value="WEEKLY">
                Weekly
              </Radio>
            </RadioGroup>
          )}
        />
      </LayoutColumn>
      {
        frequencyValue === 'DAILY'
          ? <DailyPreferences error={errors?.deliveryDays} register={register}/>
          : <WeeklyPreference control={control}/>
      }

    </>
  )
}

const ThreatProfileDropdown = ({ profiles, control, errors }) => {
  const [inputValue, setInputValue] = React.useState('')
  const filterItemsByInputValue = React.useMemo(() => {
    if (!profiles) return []
    return profiles.filter(item => item.label.toLowerCase().includes(inputValue.toLowerCase()))
  }, [inputValue, profiles])

  return (
    <>
      <Label htmlFor='tpName'>
        Threat Profile
      </Label>
      <Controller
        control={control}
        rules={{ required: true }}
        name='tpName'
        render={({
          field: { onChange, value, ref },
        }) => (
          <DropDownSelect
            onChange={onChange}
            options={filterItemsByInputValue}
            value={value}
            ref={ref}
            variant={errors?.tpName ? 'error' : undefined}
            placeholder='Please Select a Profile'
            menuSearch={
              <MenuBox>
                <SearchInputGroup
                  flex="1"
                  inputProps={{
                    onChange: (e) => setInputValue(e.target.value),
                    value: inputValue,
                    placeholder: 'Filter by name'
                  }}
                />
              </MenuBox>
            }
          />
        )}
      />
    </>
  )
}

export const ThreatProfileFormContent = ({ register, control, threatProfiles, deliveryFreq, errors, clearErrors, setValue }) => {
  return (
    <LayoutWrapper>
      <LayoutColumn maxWidth="256px" width='100%'>
        <LayoutColumn gap='spaceGapSmall' mb='spaceGapXLarge'>
          <Label htmlFor='name'>
            Name
          </Label>
          <Input size="medium" name='name' id='name' {...register('name', { required: true })} variant={errors?.name || errors?.root?.serverError ? 'error' : 'primary'}/>
        </LayoutColumn>
        <LayoutColumn gap='spaceGapSmall' mb='spaceGapXLarge'>
          <Label htmlFor='emailAddress'>
            Mailing to
          </Label>
          <Input size="medium" name='emailAddress' id='emailAddress' {...register('emailAddress')} disabled/>
        </LayoutColumn>
        <LayoutColumn gap='spaceGapSmall' mb='spaceGapXLarge'>
          <ThreatProfileDropdown profiles={threatProfiles} control={control} errors={errors}/>
        </LayoutColumn>
      </LayoutColumn>
      <LayoutColumn minWidth="445px">
        <Frequency
          control={control}
          register={register}
          deliveryFreq={deliveryFreq}
          errors={errors}
          clearErrors={clearErrors}
          setValue={setValue}
        />
        <LayoutWrapper flex='1'>
          <LayoutColumn gap='spaceGapSmall' mb='spaceGapXLarge'>
            <Label htmlFor='deliveryTime'>
              Delivery Time
            </Label>
            <Controller
              control={control}
              name='deliveryTime'
              render={({
                field: { onChange, value, ref, name },
              }) => (
                <DropDownSelect
                  onChange={onChange}
                  options={TIME_SLOTS}
                  value={value}
                  ref={ref}
                  name={name}
                />
              )}
            />
            <ControlledCheckbox
              name='enableDayLightSavings'
              control={control}
              label='Ignore Daylight Savings'
              value='dst'
            />
          </LayoutColumn>
          <LayoutColumn gap='spaceGapSmall' mb='spaceGapXLarge' flex="1">
            <Label htmlFor='timeZone'>
              Timezone
            </Label>
            <Controller
              control={control}
              name='timeZone'
              render={({
                field: { onChange, value, ref, name },
              }) => (
                <DropDownSelect
                  onChange={onChange}
                  options={TIME_ZONES}
                  value={value}
                  ref={ref}
                  name={name}
                />
              )}
            />
          </LayoutColumn>
        </LayoutWrapper>
      </LayoutColumn>
    </LayoutWrapper>
  )
}
